import { Search } from "lucide-react";
import type { ClaimBasisResponse } from "@patent-studio/api-types";
import { TipChip } from "@/components/ui/HoverTip";
import { MicroLabel } from "@/components/ui/MicroLabel";
import { VERDICT_LABELS, VERDICT_ON_TIP } from "@/lib/verdict-labels";
import { cn } from "@/lib/utils";

const REMOVED = "Position removed at Novelty";

/**
 * The positions a claim rests on, each with its own standing verdict, on the
 * design's dark tip over a small chip. The claim's verdict is read here rather
 * than beside its caption.
 *
 * A claim added by hand rests on no position, and so does one whose positions
 * were all removed at Novelty. There is nothing to read on either, so the chip
 * stays away rather than opening on a heading over nothing.
 */
export function PositionBasis({
  claimId,
  bases,
}: {
  claimId: string;
  bases: ClaimBasisResponse[];
}) {
  if (bases.length === 0) return null;
  const tip = (
    <span data-testid={`position-basis-${claimId}`} className="contents">
      <MicroLabel size="sm" className="font-extrabold text-white/60">
        Position from Novelty
      </MicroLabel>
      {bases.map((basis) => (
        <span key={basis.position_id} className="flex flex-col gap-1.5">
          <span className="text-[11.5px] leading-[1.4] font-medium">
            {basis.removed ? REMOVED : basis.title}
          </span>
          <span
            data-testid="verdict-on-tip"
            data-verdict={basis.verdict}
            className={cn(
              "inline-flex h-[19px] items-center self-start rounded-inset bg-white/15 px-[7px] text-[9.5px] font-bold",
              VERDICT_ON_TIP[basis.verdict],
            )}
          >
            {VERDICT_LABELS[basis.verdict]}
          </span>
        </span>
      ))}
    </span>
  );
  return (
    <TipChip
      label="Position basis"
      icon={<Search aria-hidden />}
      tip={tip}
      tone="dark"
      chip="quiet"
    />
  );
}
